import type { QueryClient } from '@tanstack/react-query';
import type { i18n as I18n } from 'i18next';
import { toast } from 'sonner';

import type { RealtimeManager } from '@/shared/realtime';

import {
  INBOX_CHANGED,
  NOTIFICATION_CREATED,
  notificationKeys,
  prependCachedNotification,
  setUnreadCount,
  type InboxChangedPayload,
  type Notification,
  type NotificationCreatedPayload,
} from './api';
import { navigateTo, showDesktopNotification, useNotificationPrefs } from './prefs';
import { moduleLabel, renderNotificationText } from './render';

function isCreatedPayload(value: unknown): value is NotificationCreatedPayload {
  return typeof value === 'object' && value !== null && 'grouped' in value;
}

function isChangedPayload(value: unknown): value is InboxChangedPayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { unreadCount?: unknown }).unreadCount === 'number'
  );
}

/** Tells the user about a new notification: a pop-up while the tab is visible, a desktop notification otherwise. */
function announce(notification: Notification, i18n: I18n): void {
  const text = renderNotificationText(notification, i18n);
  const open = () => {
    if (notification.link) navigateTo(notification.link);
  };
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    showDesktopNotification({
      title: notification.actor?.fullName ?? moduleLabel(notification.module, i18n),
      body: text,
      tag: notification.id,
      icon: notification.actor?.avatarUrl,
      onClick: open,
    });
    return;
  }
  if (!useNotificationPrefs.getState().popups) return;
  toast(text, {
    id: `notification:${notification.id}`,
    description: moduleLabel(notification.module, i18n),
    action: notification.link ? { label: i18n.t('notifications:actions.open'), onClick: open } : undefined,
  });
}

/**
 * Keeps the notification caches live: a new notification goes on top of the cached lists with the server's unread
 * count; changes made elsewhere (another tab or device) refresh the counter and the lists.
 */
export function registerNotificationRealtime({
  queryClient,
  realtime,
  i18n,
}: {
  queryClient: QueryClient;
  realtime: RealtimeManager;
  i18n: I18n;
}): void {
  realtime.on(NOTIFICATION_CREATED, (payload) => {
    if (!isCreatedPayload(payload)) return;
    if (payload.unreadCount !== null) setUnreadCount(queryClient, payload.unreadCount);
    else void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount });
    if (payload.notification) {
      prependCachedNotification(queryClient, payload.notification);
      announce(payload.notification, i18n);
    } else {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.lists });
    }
    void queryClient.invalidateQueries({ queryKey: notificationKeys.modules });
  });

  realtime.on(INBOX_CHANGED, (payload) => {
    if (!isChangedPayload(payload)) return;
    setUnreadCount(queryClient, payload.unreadCount);
    void queryClient.invalidateQueries({ queryKey: notificationKeys.lists });
    void queryClient.invalidateQueries({ queryKey: notificationKeys.modules });
  });
}
